import React, { useState, useEffect, useMemo } from "react";
import { Search, ArrowUp, ArrowDown, ArrowUpDown, Save, Download, Copy, Filter, Check, Settings, X, RefreshCw, Edit, Trash2, AlertTriangle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { exportToExcel } from "../utils/exportToExcel";
import { getPriceManagementSkus, bulkUpdatePriceManagementSkus, deleteRecipeSku } from "../api/sku";
import { getCostingSettings } from "../api/costingSettingsApi";
import PricePrivateLabel from "./PricePrivateLabel";

const STATIC_HEADERS = [
  "Wholesale 3",
  "Wholesale 2",
  "Wholesale 1",
  "Standard SRP",
  "Amazon Retail",
  "Discount CA",
  "Discount USA",
  "Distributor",
  "Web Retail",
  "Export MENA",
  "Export USD"
];

const RETAIL_CHANNEL_RULES = [
  { _id: "standard_srp", priceLevel: "Standard SRP", targetNetMargin: 50 },
  { _id: "amazon_retail", priceLevel: "Amazon Retail", targetNetMargin: 50 },
  { _id: "discount_ca", priceLevel: "Discount CA", targetNetMargin: 45 },
  { _id: "discount_usa", priceLevel: "Discount USA", targetNetMargin: 45 },
  { _id: "distributor", priceLevel: "Distributor", targetNetMargin: 25 },
  { _id: "export_mena", priceLevel: "Export MENA", targetNetMargin: 30 },
  { _id: "export_usd", priceLevel: "Export USD", targetNetMargin: 30 },
  { _id: "web_retail", priceLevel: "Web Retail", targetNetMargin: 50 },
  { _id: "wholesale_1", priceLevel: "Wholesale 1", targetNetMargin: 40 },
  { _id: "wholesale_2", priceLevel: "Wholesale 2", targetNetMargin: 35 },
  { _id: "wholesale_3", priceLevel: "Wholesale 3", targetNetMargin: 30 },
];

export default function PriceManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("existing");
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currency, setCurrency] = useState("CAD");
  const [updating, setUpdating] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // New States
  const [channelRules, setChannelRules] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [priceChangeFilter, setPriceChangeFilter] = useState("all");
  const [marginThreshold, setMarginThreshold] = useState(5);
  const [strategies, setStrategies] = useState({});
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('priceManagement_columnVisibility');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { }
    }
    return {
      sku: true,
      name: true,
      category: true,
      strategy: true,
      cost: true,
    };
  });

  const [sortBy, setSortBy] = useState("sku");
  const [sortOrder, setSortOrder] = useState("asc");
  const [skuFilter, setSkuFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [pricingSegment, setPricingSegment] = useState(() => {
    if (location.state?.quotationData) return "privatelabel";
    return "retail";
  });
  const [segmentCategoryMappings, setSegmentCategoryMappings] = useState({ bulk: [], retail: [], privatelabel: [] });
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [deletingSkuItem, setDeletingSkuItem] = useState(null);
  const [isDeletingSku, setIsDeletingSku] = useState(false);
  const [hiddenCategories, setHiddenCategories] = useState(() => {
    const saved = localStorage.getItem('priceManagement_hiddenCategories');
    if (saved) {
      try { return [...new Set(JSON.parse(saved))]; } catch (e) { }
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('priceManagement_columnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    localStorage.setItem('priceManagement_hiddenCategories', JSON.stringify(hiddenCategories));
  }, [hiddenCategories]);

  useEffect(() => {
    if (channelRules.length > 0) {
      setColumnVisibility(prev => {
        let updated = { ...prev };
        let hasChanges = false;
        channelRules.forEach(rule => {
          if (updated[rule._id] === undefined) {
            updated[rule._id] = true;
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
    }
  }, [channelRules]);

  // Hardcoded Admin Flag
  const isAdmin = true;

  const filteredAvailableCategories = useMemo(() => {
    if (pricingSegment && segmentCategoryMappings[pricingSegment]) {
      // Strictly only include categories mapped to this segment.
      return availableCategories.filter(cat => segmentCategoryMappings[pricingSegment].includes(cat));
    }
    return availableCategories;
  }, [availableCategories, segmentCategoryMappings, pricingSegment]);

  // The categories to show as quick pills (top 3)
  const quickCategories = useMemo(() => {
    return filteredAvailableCategories.slice(0, 3);
  }, [filteredAvailableCategories]);

  useEffect(() => {
    const init = async () => {
      const settingsRes = await getCostingSettings();
      if (settingsRes?.success && settingsRes?.data?.segmentCategoryMappings) {
        setSegmentCategoryMappings(settingsRes.data.segmentCategoryMappings);
      }
    };
    init();
  }, []);

  const filteredCategoriesStr = filteredAvailableCategories.join(",");

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSkus();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [activeTab, search, selectedCategories, limit, page, currency, sortBy, sortOrder, skuFilter, nameFilter, hiddenCategories, pricingSegment, segmentCategoryMappings, filteredCategoriesStr]);

  useEffect(() => {
    setPage(1);
    setSelectedSkus([]); // Clear selection when filters change
  }, [activeTab, search, selectedCategories, limit, skuFilter, nameFilter, pricingSegment, segmentCategoryMappings, filteredCategoriesStr]);

  useEffect(() => {
    setSelectedCategories([]);
  }, [pricingSegment]);

  const handleDeleteSku = async (item) => {
    if (!item?.sku) return;
    setIsDeletingSku(true);
    try {
      const res = await deleteRecipeSku(item.sku);
      toast.success(res?.message || `SKU ${item.sku} deleted successfully from all collections!`, { duration: 5000 });
      setDeletingSkuItem(null);
      fetchSkus();
    } catch (err) {
      console.error("Delete SKU error:", err);
      toast.error(err?.message || "Failed to delete SKU");
    } finally {
      setIsDeletingSku(false);
    }
  };

  const fetchSkus = async () => {
    setLoading(true);
    try {
      const filter = activeTab === "new" ? "new" : "";

      const segmentHiddenCategories = [...new Set(hiddenCategories
        .filter(c => c.startsWith(`${pricingSegment}:`))
        .map(c => c.split(':')[1]))];

      let categoriesParam = selectedCategories.join(",");
      if (selectedCategories.length === 0) {
        const mappedForSegment = segmentCategoryMappings[pricingSegment] || [];
        if (mappedForSegment.length === 0) {
          categoriesParam = "__NONE__";
        } else {
          const visibleMapped = mappedForSegment.filter(cat => !segmentHiddenCategories.includes(cat));
          categoriesParam = visibleMapped.join(",");
          if (categoriesParam === "") categoriesParam = "__NONE__";
        }
      } else {
        const visibleSelected = selectedCategories.filter(cat => !segmentHiddenCategories.includes(cat));
        categoriesParam = visibleSelected.join(",");
        if (categoriesParam === "") categoriesParam = "__NONE__";
      }

      const params = {
        filter, search, page, limit,
        sortBy, sortOrder, skuFilter, nameFilter,
        segment: pricingSegment,
        categories: categoriesParam
      };
      const data = await getPriceManagementSkus(params);

      if (data.success) {
        setTotalItems(data.pagination?.total || 0);
        setTotalPages(data.pagination?.totalPages || 1);
        if (data.channelRules) setChannelRules(data.channelRules);
        if (data.categories) setAvailableCategories(data.categories);

        setSkus(data.data || []);
      } else {
        toast.error("Failed to fetch SKUs");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error loading SKUs");
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = (cost, targetNetMargin, strategy, threshold) => {
    const baseMargin = (targetNetMargin || 0) / 100;
    let adjustedMargin = baseMargin;
    if (strategy === "Min") adjustedMargin = baseMargin - (threshold / 100);
    if (strategy === "Max") adjustedMargin = baseMargin + (threshold / 100);

    // Prevent division by zero or negative prices
    if (adjustedMargin >= 1) adjustedMargin = 0.99;

    return cost > 0 ? cost / (1 - adjustedMargin) : 0;
  };

  const getOldPriceForLevel = (sku, levelName) => {
    // A simplified fallback: return base msrpCad/Usd
    if (currency === "CAD") return sku.msrpCad || 0;
    if (currency === "USD") return sku.msrpUsd || 0;
    if (currency === "EUR") return sku.msrpEur || 0;
    if (currency === "AED") return sku.msrpAed || 0;
    return 0;
  };

  const processedSkus = useMemo(() => {
    const segmentHiddenCategories = hiddenCategories
      .filter(c => c.startsWith(`${pricingSegment}:`))
      .map(c => c.split(':')[1]);

    let allowedCategories = selectedCategories.length > 0 ? selectedCategories : (segmentCategoryMappings[pricingSegment] || []);
    if (allowedCategories.length === 0) {
      allowedCategories = ["__NONE__"];
    }

    let result = skus.filter(sku => {
      // Bypass category filters if there's an active search string, 
      // but strictly filter the result locally to ensure only relevant items are shown.
      if (search && search.trim() !== "") {
        const lowerSearch = search.trim().toLowerCase();
        const skuStr = String(sku.sku || "").toLowerCase();
        const nameStr = String(sku.customFields?.nameEn || "").toLowerCase();

        if (!skuStr.includes(lowerSearch) && !nameStr.includes(lowerSearch)) {
          return false;
        }
        return true;
      }

      // Bypass category filters on the "New Items" tab since new SKUs might not be mapped yet
      if (activeTab === 'new') {
        return true;
      }

      if (segmentHiddenCategories.includes(sku.category)) return false;

      if (allowedCategories.includes("__NONE__")) return false;
      if (allowedCategories.length > 0 && !allowedCategories.includes(sku.category)) return false;

      return true;
    }).map(sku => {
      const supplierCost = Array.isArray(sku.suppliers) && sku.suppliers.length > 0
        ? (sku.suppliers[0].landingCost || sku.suppliers[0].purchasePrice)
        : (sku.suppliers ? sku.suppliers.landingCost || sku.suppliers.purchasePrice : 0);

      const cost = sku.costing?.mfgCostPerKg ||
        sku.mfgCostPerKg ||
        sku.costing?.acctCostPerKg ||
        supplierCost ||
        sku.pmCostPerKgCad ||
        0;
      const strategy = strategies[sku.sku] || "Regular";

      const priceLevels = channelRules.map(rule => {
        let newPrice = 0;
        let masterPrice = 0;
        let marginPercentage = rule.targetNetMargin || 0;
        let pricePerLbs = 0;
        let savedPrice = 0;

        if (sku.costSummaryPrices && Array.isArray(sku.costSummaryPrices)) {
          const savedPriceData = sku.costSummaryPrices.find(
            (p) => p.metric && rule.priceLevel && p.metric.toLowerCase() === rule.priceLevel.toLowerCase()
          );
          if (savedPriceData) {
            savedPrice = savedPriceData.pricePerKg || 0;
            masterPrice = savedPriceData.masterPrice || 0;
            marginPercentage = savedPriceData.marginPercentage || 0;
            pricePerLbs = savedPriceData.pricePerLbs || 0;
          }
        }

        // Dynamically calculate new price based on current cost
        let calculatedNewPrice = calculatePrice(cost, rule.targetNetMargin, strategy, marginThreshold);

        // If cost is 0, calculated price is 0. In this case, fallback newPrice to the saved price.
        newPrice = calculatedNewPrice > 0 ? calculatedNewPrice : savedPrice;

        // Old price is the saved database price, or fallback to MSRP if not saved
        const oldPrice = savedPrice > 0 ? savedPrice : getOldPriceForLevel(sku, rule.priceLevel);
        const diff = newPrice - oldPrice;

        // Recalculate margin for the actual newPrice if we have a valid cost, otherwise use saved margin
        if (cost > 0 && newPrice > 0) {
          marginPercentage = ((newPrice - cost) / newPrice) * 100;
        }

        const grossMargin = newPrice > 0 && cost > 0 ? ((newPrice - cost) / newPrice) * 100 : 0;


        return {
          id: rule._id,
          name: rule.priceLevel,
          oldPrice,
          newPrice,
          diff,
          margin: marginPercentage,
          masterPrice,
          pricePerLbs,
        };
      });

      // We use the first price level to determine "Increase/Decrease" overall for the filter
      const primaryDiff = priceLevels.length > 0 ? priceLevels[0].diff : 0;
      const primaryOldPrice = priceLevels.length > 0 ? priceLevels[0].oldPrice : 0;
      const primaryChangePct = primaryOldPrice > 0 ? (primaryDiff / primaryOldPrice) * 100 : (primaryDiff > 0 ? 100 : 0);

      return {
        ...sku,
        cost,
        strategy,
        priceLevels,
        primaryDiff,
        primaryChangePct
      };
    });

    if (priceChangeFilter === "increase") {
      result = result.filter(s => s.primaryDiff > 0);
    } else if (priceChangeFilter === "decrease") {
      result = result.filter(s => s.primaryDiff < 0);
    } else if (priceChangeFilter === "no_change") {
      result = result.filter(s => s.primaryDiff === 0);
    } else if (priceChangeFilter === "over_threshold") {
      result = result.filter(s => s.primaryChangePct > marginThreshold);
    } else if (priceChangeFilter === "under_threshold") {
      result = result.filter(s => s.primaryChangePct < -marginThreshold);
    }

    return result;
  }, [skus, channelRules, strategies, marginThreshold, priceChangeFilter, currency]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleBulkUpdate = async () => {
    setUpdating(true);
    try {
      // Create updates using the first price level for MSRP (simplification)
      const updates = processedSkus.map(s => {
        const primaryNewPrice = s.priceLevels.length > 0 ? s.priceLevels[0].newPrice : 0;
        return {
          sku: s.sku,
          msrpCad: currency === "CAD" ? primaryNewPrice : s.msrpCad,
          msrpUsd: currency === "USD" ? primaryNewPrice : s.msrpUsd,
          msrpEur: currency === "EUR" ? primaryNewPrice : s.msrpEur,
          msrpAed: currency === "AED" ? primaryNewPrice : s.msrpAed,
        };
      });

      const data = await bulkUpdatePriceManagementSkus(updates);
      if (data.success) {
        toast.success(data.message);
        fetchSkus();
      } else {
        toast.error(data.message || "Failed to update prices");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating prices");
    } finally {
      setUpdating(false);
    }
  };

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleResetFilters = () => {
    setSearch("");
    setSelectedCategories([]);
    setPriceChangeFilter("all");
    setMarginThreshold(5);
    setCurrency("CAD");
  };

  const copyColumn = (columnType) => {
    let values = [];
    if (columnType === 'sku') values = processedSkus.map(s => s.sku);
    else if (columnType === 'name') values = processedSkus.map(s => s.customFields?.nameEn || "-");
    else if (columnType === 'category') values = processedSkus.map(s => s.category || "-");
    else {
      values = processedSkus.map(sku => {
        const level = sku.priceLevels.find(l => l.id === columnType);
        return level ? level.newPrice.toFixed(2) : "0.00";
      });
    }
    navigator.clipboard.writeText(values.join("\n"));
    toast.success("Column copied to clipboard!");
  };

  const handleExport = () => {
    let exportSkus = processedSkus;
    if (selectedSkus.length > 0) {
      exportSkus = processedSkus.filter(s => selectedSkus.includes(s.sku));
    }

    if (exportSkus.length === 0) {
      toast.error("No SKUs to export");
      return;
    }
    const exportData = exportSkus.map(s => {
      const row = {
        SKU: s.sku,
        "Name EN": s.customFields?.nameEn || "-",
        Category: s.category,
        "Strategy": s.strategy,
        "Price": isAdmin ? s.cost.toFixed(2) : "***",
      };

      s.priceLevels.forEach(level => {
        row[`${level.name} Price`] = level.newPrice.toFixed(2);
        row[`${level.name} Margin %`] = level.margin.toFixed(2) + "%";
      });
      return row;
    });
    exportToExcel(exportData, `Price_Management_${new Date().toISOString().slice(0, 10)}`);
  };

  const startItem = totalItems === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(totalPages, page + 2);
    if (page <= 3) endPage = Math.min(5, totalPages);
    if (page >= totalPages - 2) startPage = Math.max(1, totalPages - 4);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const hasVisibleColumns = Object.values(columnVisibility).some(Boolean);

  return (
    <div className="w-full font-sans mb-5 px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Light Header Area */}
        <div className="bg-white border-b border-gray-100 pt-6 px-6">
          {/* Top Row: Title & Actions */}
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
            <div>
              <div
                className="text-[11px] font-bold tracking-widest mb-1"
                style={{ color: pricingSegment === 'bulk' ? '#0f021bff' : pricingSegment === 'retail' ? '#0e0a02ff' : '#020e06ff' }}
              >
                PRICING ENGINE • {pricingSegment === 'bulk' ? 'BULK' : pricingSegment === 'retail' ? 'RETAIL' : 'PRIVATE LABEL'}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Price Management</h2>
            </div>

            <div className="flex gap-4 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Threshold ±%:</label>
                <input
                  type="number"
                  value={marginThreshold}
                  onChange={(e) => setMarginThreshold(Number(e.target.value))}
                  className="w-16 px-2 py-1.5 border border-gray-300 bg-white text-gray-900 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 font-semibold text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 cursor-pointer appearance-none pr-8 relative"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: "right 0.5rem center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "1.5em 1.5em"
                }}
              >
                <option value="CAD">Currency: CAD</option>
                <option value="USD">Currency: USD</option>
                <option value="EUR">Currency: EURO</option>
                <option value="AED">Currency: AED</option>
              </select>
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-2 border border-gray-300 rounded-xl bg-white text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition"
                title="Settings"
              >
                <Settings size={20} />
              </button>
            </div>
          </div>

          {/* Segment Buttons */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => setPricingSegment("bulk")}
              className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${pricingSegment === "bulk" ? "bg-black text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              Bulk
            </button>
            <button
              onClick={() => setPricingSegment("retail")}
              className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${pricingSegment === "retail" ? "bg-black text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              Retail
            </button>
            <button
              onClick={() => setPricingSegment("privatelabel")}
              className={`px-5 py-2 rounded-lg font-semibold text-sm transition-colors ${pricingSegment === "privatelabel" ? "bg-black text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              Private Label
            </button>
          </div>

          {/* Sub-tabs */}
          {pricingSegment !== 'privatelabel' && (
            <div className="flex items-center gap-8 border-b border-gray-200 mb-2">
              <button
                onClick={() => setActiveTab("existing")}
                className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === "existing" ? "border-black text-black" : "border-transparent text-gray-500 hover:text-gray-700"}`}
              >
                Price Updates
              </button>
              <button
                onClick={() => setActiveTab("new")}
                className={`pb-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === "new" ? "border-black text-black" : "border-transparent text-gray-500 hover:text-gray-700"}`}
              >
                New Items
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        {pricingSegment === 'privatelabel' ? (
          // <div className="p-6 h-[400px] flex flex-col justify-center items-center">
          //   <div className="flex flex-col items-center justify-center p-12 text-gray-500 bg-gray-50 rounded-xl border border-gray-200 border-dashed w-full max-w-2xl">
          //     <span className="text-lg font-medium text-gray-700">Private Label Pricing</span>
          //     <p className="mt-2 text-sm text-center">This section is currently under development.</p>
          //   </div>
          // </div>
          <PricePrivateLabel />
        ) : (
          <div className="p-6">
            <div className="flex flex-col gap-4 mb-6">
              {/* Top Row */}
              <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 w-full xl:w-auto">
                  <div className="relative w-full md:w-[350px] shrink-0">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search SKU or name..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-300 focus:ring-0"
                    />
                  </div>
                </div>


                <div className="flex flex-wrap items-center gap-2 shrink-0 w-full xl:w-auto mt-4 xl:mt-0">
                  <button
                    onClick={fetchSkus}
                    disabled={!hasVisibleColumns}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white font-medium text-sm hover:bg-gray-50 text-gray-700 shrink-0 w-full md:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Refresh Data"
                  >
                    <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
                  </button>
                  <button
                    onClick={handleResetFilters}
                    disabled={!hasVisibleColumns}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white font-medium text-sm hover:bg-gray-50 text-gray-700 shrink-0 w-full md:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Reset Filters
                  </button>
                  <button
                    onClick={handleExport}
                    disabled={processedSkus.length === 0 || !hasVisibleColumns}
                    className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium text-sm hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed shrink-0 shadow-sm w-full md:w-auto justify-center"
                  >
                    <Download size={16} /> Export
                  </button>
                  <button
                    onClick={handleBulkUpdate}
                    disabled={true}
                    className="flex items-center gap-2 px-6 py-2 rounded-lg bg-black text-white font-medium text-sm hover:bg-gray-900 transition disabled:opacity-50 shrink-0 shadow-sm w-full md:w-auto justify-center"
                  >
                    <Save size={16} /> {updating ? "Updating..." : pricingSegment === 'bulk' ? "Bulk Update" : pricingSegment === 'retail' ? "Retail Update" : "Private Label Update"}
                  </button>
                </div>
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-6 flex-wrap">
                <button
                  onClick={() => setPriceChangeFilter("all")}
                  disabled={!hasVisibleColumns}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${priceChangeFilter === "all" ? "bg-[#111827] text-white border border-[#111827]" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                >
                  All
                </button>
                <button
                  onClick={() => setPriceChangeFilter("increase")}
                  disabled={!hasVisibleColumns}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${priceChangeFilter === "increase" ? "bg-[#111827] text-white border border-[#111827]" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                >
                  Increases
                </button>
                <button
                  onClick={() => setPriceChangeFilter("decrease")}
                  disabled={!hasVisibleColumns}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${priceChangeFilter === "decrease" ? "bg-[#111827] text-white border border-[#111827]" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                >
                  Decreases
                </button>
                <button
                  onClick={() => setPriceChangeFilter("under_threshold")}
                  disabled={!hasVisibleColumns}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${priceChangeFilter === "under_threshold" ? "bg-[#111827] text-white border border-[#111827]" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                >
                  Under Threshold
                </button>
                <button
                  onClick={() => setPriceChangeFilter("over_threshold")}
                  disabled={!hasVisibleColumns}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed ${priceChangeFilter === "over_threshold" ? "bg-[#111827] text-white border border-[#111827]" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}
                >
                  Over Threshold
                </button>
              </div>

              {/* Bottom Row */}
              <div className="flex items-center gap-2 flex-wrap">
                {quickCategories.filter(cat => !hiddenCategories.includes(`${pricingSegment}:${cat}`)).map(cat => (
                  <div key={cat} className="flex items-center">
                    <button
                      onClick={() => toggleCategory(cat)}
                      disabled={!hasVisibleColumns}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed ${selectedCategories.includes(cat)
                        ? "bg-[#111827] text-white border-[#111827]"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                        }`}
                    >
                      {cat}
                    </button>
                  </div>
                ))}

                {filteredAvailableCategories.length > quickCategories.length && (
                  <div className="relative">
                    <button
                      onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                      disabled={!hasVisibleColumns}
                      className="px-4 py-1.5 rounded-full text-sm font-medium border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 flex items-center gap-1 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      More {(() => {
                        const selCount = selectedCategories.filter(cat => !quickCategories.includes(cat) && !hiddenCategories.includes(`${pricingSegment}:${cat}`)).length;
                        return selCount > 0 ? `(${selCount})` : "";
                      })()} <span className="text-[10px] ml-1">▼</span>
                    </button>
                    {isCategoryDropdownOpen && (
                      <div className="absolute top-full left-0 mt-2 w-64 max-h-60 overflow-y-auto bg-white border border-gray-200 shadow-xl rounded-xl z-50 p-2">
                        {filteredAvailableCategories.filter(cat => !hiddenCategories.includes(`${pricingSegment}:${cat}`)).map(cat => (
                          <div key={cat} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer flex-1">
                              <input
                                type="checkbox"
                                checked={selectedCategories.includes(cat)}
                                onChange={() => toggleCategory(cat)}
                                className="rounded border-gray-300 text-black focus:ring-black"
                              />
                              <span className="text-sm text-gray-700">{cat}</span>
                            </label>
                            <button
                              onClick={() => setHiddenCategories([...new Set([...hiddenCategories, `${pricingSegment}:${cat}`])])}
                              className="text-gray-400 hover:text-red-500 transition px-2"
                              title="Hide Category"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Table or Empty State */}
            {!hasVisibleColumns ? (
              <div className="flex flex-col items-center justify-center p-12 text-gray-500 bg-gray-50 rounded-xl border border-gray-200 border-dashed h-64">
                <span className="text-lg font-medium text-gray-700">No Columns Visible</span>
                <p className="mt-2 text-sm text-center max-w-md">
                  All data columns have been disabled. Please enable at least one column from the settings menu to view the data.
                </p>
                <button
                  onClick={() => setIsSettingsOpen(true)}
                  className="mt-4 px-4 py-2 border border-gray-300 rounded-lg bg-white font-medium text-sm hover:bg-gray-50 text-gray-700 transition"
                >
                  Open Settings
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {hasVisibleColumns && (
                      <th className="px-4 py-3 font-semibold text-gray-600 sticky left-0 bg-gray-50 z-20 shadow-[1px_0_0_0_#e5e7eb] w-12 text-center">
                        <input
                          type="checkbox"
                          checked={processedSkus.length > 0 && selectedSkus.length === processedSkus.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSkus(processedSkus.map(s => s.sku));
                            } else {
                              setSelectedSkus([]);
                            }
                          }}
                          className="rounded border-gray-300 text-black focus:ring-black cursor-pointer"
                        />
                      </th>
                    )}
                    {columnVisibility.sku && (
                      <th className="px-4 py-3 font-semibold text-gray-600 sticky left-12 bg-gray-50 z-20 shadow-[1px_0_0_0_#e5e7eb] min-w-[150px] whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleSort('sku')} className="flex items-center gap-1 hover:text-black transition">
                            SKU
                            {sortBy === 'sku' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} className="opacity-40" />}
                          </button>
                          <button onClick={() => copyColumn('sku')} className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-700 transition" title="Copy SKU column">
                            <Copy size={14} />
                          </button>
                        </div>
                      </th>
                    )}
                    {columnVisibility.name && (
                      <th className="px-4 py-3 font-semibold text-gray-600 min-w-[200px] whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-black transition">
                            Name EN
                            {sortBy === 'name' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} className="opacity-40" />}
                          </button>
                          <button onClick={() => copyColumn('name')} className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-700 transition" title="Copy Name column">
                            <Copy size={14} />
                          </button>
                        </div>
                      </th>
                    )}
                    {columnVisibility.category && (
                      <th className="px-4 py-3 font-semibold text-gray-600 min-w-[150px] whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleSort('category')} className="flex items-center gap-1 hover:text-black transition">
                            Category
                            {sortBy === 'category' ? (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ArrowUpDown size={14} className="opacity-40" />}
                          </button>
                          <button onClick={() => copyColumn('category')} className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-700 transition" title="Copy Category column">
                            <Copy size={14} />
                          </button>
                        </div>
                      </th>
                    )}
                    {columnVisibility.strategy && <th className="px-4 py-3 font-semibold text-gray-600 align-top whitespace-nowrap">Strategy</th>}
                    {isAdmin && columnVisibility.cost && <th className="px-4 py-3 font-semibold text-gray-600 text-right whitespace-nowrap">Price</th>}

                    {STATIC_HEADERS.map(header => {
                      const rule = channelRules.find(r => r.priceLevel.trim().toLowerCase() === header.trim().toLowerCase());
                      const colId = rule ? rule._id : header;
                      if (columnVisibility[colId] === false) return null;
                      return (
                        <th key={colId} className="px-4 py-3 font-semibold text-gray-600 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            {header}
                            {rule && (
                              <button
                                onClick={() => copyColumn(colId)}
                                className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-700 transition"
                                title="Copy column"
                              >
                                <Copy size={14} />
                              </button>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="text-center py-10 text-gray-500">Loading SKUs...</td>
                    </tr>
                  ) : processedSkus.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-10 text-gray-500">No SKUs found.</td>
                    </tr>
                  ) : (
                    processedSkus.map((sku) => (
                      <tr key={sku._id} className="hover:bg-gray-50 transition group">
                        {hasVisibleColumns && (
                          <td className="px-4 py-3 sticky left-0 bg-white group-hover:bg-gray-50 z-10 shadow-[1px_0_0_0_#e5e7eb] text-center">
                            <input
                              type="checkbox"
                              checked={selectedSkus.includes(sku.sku)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSkus([...selectedSkus, sku.sku]);
                                } else {
                                  setSelectedSkus(selectedSkus.filter(s => s !== sku.sku));
                                }
                              }}
                              className="rounded border-gray-300 text-black focus:ring-black cursor-pointer"
                            />
                          </td>
                        )}
                        {columnVisibility.sku && (
                          <td className="px-4 py-3 sticky left-12 bg-white group-hover:bg-gray-50 z-10 shadow-[1px_0_0_0_#e5e7eb]">
                            <div className="flex items-center gap-2">
                              <div className="font-mono text-gray-900 font-medium">{sku.sku}</div>
                              <button
                                onClick={() => navigate(`/costing-tool?edit_sku=${encodeURIComponent(sku.sku)}`)}
                                className="text-gray-400 hover:text-blue-600 transition"
                                title="Edit SKU"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeletingSkuItem(sku)}
                                className="text-gray-400 hover:text-red-600 transition p-0.5 ml-0.5"
                                title="Delete SKU Everywhere"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        )}
                        {columnVisibility.name && (
                          <td
                            className="px-4 py-3 text-xs text-gray-700 max-w-[320px] font-medium leading-snug"
                            title={sku.customFields?.nameEn || ""}
                          >
                            <span className="line-clamp-2 hover:line-clamp-none cursor-default">
                              {sku.customFields?.nameEn || "-"}
                            </span>
                          </td>
                        )}
                        {columnVisibility.category && (
                          <td
                            className="px-4 py-3 text-xs text-gray-600 max-w-[200px]"
                            title={sku.category || ""}
                          >
                            <span className="truncate block">
                              {sku.category || "-"}
                            </span>
                          </td>
                        )}
                        {columnVisibility.strategy && (
                          <td className="px-4 py-3">
                            <select
                              value={sku.strategy}
                              onChange={(e) => setStrategies(prev => ({ ...prev, [sku.sku]: e.target.value }))}
                              className="bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg focus:ring-slate-500 focus:border-slate-500 block w-24 p-1.5"
                            >
                              <option value="Min">Min</option>
                              <option value="Regular">Reg</option>
                              <option value="Max">Max</option>
                            </select>
                          </td>
                        )}

                        {isAdmin && columnVisibility.cost && (
                          <td className="px-4 py-3 text-right text-gray-600">
                            ${sku.cost.toFixed(3)}
                          </td>
                        )}

                        {STATIC_HEADERS.map(header => {
                          const rule = channelRules.find(r => r.priceLevel.trim().toLowerCase() === header.trim().toLowerCase());
                          const colId = rule ? rule._id : header;
                          if (columnVisibility[colId] === false) return null;

                          const level = rule ? sku.priceLevels.find(l => l.id === rule._id) : null;

                          return (
                            <td key={colId} className="px-4 py-3 text-right">
                              {level ? (
                                <div className="flex flex-col items-end gap-0.5">
                                  <div className="flex items-center gap-1">
                                    {level.diff > 0 && <ArrowUp size={12} className="text-green-500" />}
                                    {level.diff < 0 && <ArrowDown size={12} className="text-red-500" />}
                                    <span className="font-medium text-gray-900">${level.newPrice.toFixed(2)}</span>
                                  </div>
                                  <span className="text-[10px] text-gray-400 select-none">
                                    Mg: {level.margin.toFixed(1)}%
                                  </span>
                                  <span className="text-[10px] text-gray-500 select-none" title="Old Price">
                                    Old: ${level.oldPrice.toFixed(2)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            )}

            {/* Pagination */}
            {hasVisibleColumns && !loading && totalItems > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 px-2">
                <div className="flex items-center text-sm text-gray-500 mb-4 sm:mb-0">
                  <span>Showing <b className="text-gray-900">{startItem}–{endItem}</b> of <b className="text-gray-900">{totalItems}</b></span>
                  <span className="mx-4 text-gray-300">|</span>
                  <span className="mr-2">Per page:</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className="border border-gray-200 rounded-md p-1 focus:outline-none focus:ring-1 focus:ring-black"
                  >
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={300}>300</option>
                  </select>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white text-gray-600"
                  >
                    &lt; Prev
                  </button>

                  {getPageNumbers().map(p => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${page === p
                        ? "bg-[#0b1021] text-white"
                        : "border border-transparent text-gray-600 hover:bg-gray-100"
                        }`}
                    >
                      {p}
                    </button>
                  ))}

                  {totalPages > 5 && page < totalPages - 2 && (
                    <>
                      <span className="px-1 text-gray-400">...</span>
                      <button
                        onClick={() => setPage(totalPages)}
                        className="px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white text-gray-600 ml-1"
                  >
                    Next &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Settings Sidebar Overlay */}
      {isSettingsOpen && (
        <div className="fixed inset-0 bg-black/20 z-50 flex justify-end">
          <div className="w-[400px] bg-white h-full shadow-2xl flex flex-col animate-slide-in-right">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">PRICE MANAGEMENT SETTINGS</h3>
              <button onClick={() => setIsSettingsOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">ACTIVE COLUMNS</h4>
                {(() => {
                  const isAnyDisabled = Object.values(columnVisibility).some(v => v === false);
                  return (
                    <button
                      onClick={() => {
                        const newState = Object.keys(columnVisibility).reduce((acc, key) => ({ ...acc, [key]: isAnyDisabled }), {});
                        setColumnVisibility(newState);
                      }}
                      className="text-sm text-black font-medium hover:text-gray-700"
                    >
                      {isAnyDisabled ? "Enable all" : "Disable all"}
                    </button>
                  );
                })()}
              </div>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Only enabled columns appear in the Price Updates and New Items tables.
              </p>

              <div className="flex flex-col gap-5">
                {[
                  { key: 'sku', label: 'SKU' },
                  { key: 'name', label: 'Name EN' },
                  { key: 'category', label: 'Category' },
                  { key: 'strategy', label: 'Strategy' },
                  ...(isAdmin ? [{ key: 'cost', label: 'Price' }] : []),
                  ...STATIC_HEADERS.map(header => {
                    const rule = channelRules.find(r => r.priceLevel.trim().toLowerCase() === header.trim().toLowerCase());
                    return { key: rule ? rule._id : header, label: header };
                  })
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-[15px] text-gray-800">{label}</span>
                    <button
                      onClick={() => setColumnVisibility(prev => ({ ...prev, [key]: !prev[key] }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${columnVisibility[key] !== false ? 'bg-black' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${columnVisibility[key] !== false ? 'translate-x-5' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-10 mb-2 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">ACTIVE CATEGORIES</h4>
                {(() => {
                  const isAnyCatHidden = hiddenCategories.length > 0;
                  return (
                    <button
                      onClick={() => {
                        if (isAnyCatHidden) {
                          setHiddenCategories([]);
                        } else {
                          const allSegmentKeys = ['retail', 'bulk', 'privatelabel'].flatMap(s =>
                            (availableCategories || [])
                              .filter(c => c && c.trim() !== "" && segmentCategoryMappings[s]?.includes(c))
                              .map(c => `${s}:${c}`)
                          );
                          setHiddenCategories(allSegmentKeys);
                        }
                      }}
                      className="text-sm text-black font-medium hover:text-gray-700"
                    >
                      {isAnyCatHidden ? "Enable all" : "Disable all"}
                    </button>
                  );
                })()}
              </div>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Only enabled categories appear in the tables and filters.
              </p>

              <div className="flex flex-col gap-6">
                {['retail', 'bulk', 'privatelabel'].map(segment => {
                  let segmentCategories = [];
                  let segmentTitle = "";

                  segmentCategories = availableCategories.filter(cat => cat && cat.trim() !== "" && segmentCategoryMappings[segment]?.includes(cat));
                  segmentTitle = segment === 'retail' ? 'Retail' : segment === 'bulk' ? 'Bulk' : 'Private Label';

                  if (segmentCategories.length === 0) return null;

                  return (
                    <div key={segment}>
                      <h5 className="text-[11px] font-bold  text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">{segmentTitle} Categories</h5>
                      <div className="flex flex-col gap-4">
                        {segmentCategories.map(cat => {
                          const key = `${segment}:${cat}`;
                          return (
                            <div key={cat} className="flex items-center justify-between">
                              <span className="text-[14px] text-gray-700">{cat}</span>
                              <button
                                onClick={() => {
                                  if (hiddenCategories.includes(key)) {
                                    setHiddenCategories(hiddenCategories.filter(c => c !== key));
                                  } else {
                                    setHiddenCategories([...hiddenCategories, key]);
                                  }
                                }}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${!hiddenCategories.includes(key) ? 'bg-black' : 'bg-gray-200'
                                  }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!hiddenCategories.includes(key) ? 'translate-x-4' : 'translate-x-1'
                                    }`}
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete SKU Confirmation Modal */}
      {deletingSkuItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Delete SKU Everywhere</h3>
                <p className="text-xs text-gray-500">Permanently delete across all collections</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Are you sure you want to permanently delete SKU <strong className="font-mono text-black">{deletingSkuItem.sku}</strong> ({deletingSkuItem.customFields?.nameEn || deletingSkuItem.name || ""})?
            </p>
            <div className="bg-red-50 text-red-700 text-xs p-3 rounded-xl border border-red-200">
              ⚠️ This will remove this product completely from <strong>Price Management</strong>, <strong>New SKU History</strong>, <strong>Barcode Center</strong>, <strong>RM Database</strong>, and <strong>BOM</strong>.
            </div>
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={isDeletingSku}
                onClick={() => setDeletingSkuItem(null)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingSku}
                onClick={() => handleDeleteSku(deletingSkuItem)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-md transition active:scale-95 cursor-pointer disabled:opacity-50"
              >
                {isDeletingSku ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Yes, Delete Everywhere
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
